import { useQuery } from '@tanstack/react-query';
import { ArrowDown, ArrowUp, Check, Link2, Plus, Search, X } from 'lucide-react';
import { useState } from 'react';

import { useSiteScope } from '@/app/site/useSiteScope';
import { recordsGet, recordsList } from '@/shared/api/generated/records/records';
import { useDebounced } from '@/shared/lib/useDebounced';
import { Button } from '@/shared/ui/Button';
import { Dialog, DialogContent } from '@/shared/ui/Dialog';
import { Input } from '@/shared/ui/Input';
import { Select, SelectItem } from '@/shared/ui/Select';

import styles from './FieldInput.module.scss';

/**
 * Ссылка на запись: название выбранной и выбор из списка — коллекция,
 * поиск, щелчок. С `multiple` — список ссылок в выбранном порядке
 * (категории кейса, подборка на главной). `collection` — короткое имя
 * коллекции, из которой выбирать; пусто — из любой.
 */
export function ReferenceInput({
  value,
  multiple,
  collection,
  onChange,
}: {
  value: unknown;
  multiple: boolean;
  collection: string;
  onChange: (value: unknown) => void;
}) {
  const [picking, setPicking] = useState(false);
  const ids = idsOf(value);

  if (!multiple) {
    const id = ids[0] ?? '';

    return (
      <div className={styles['reference']}>
        {id === '' ? (
          <span className={styles['reference__empty']}>Не выбрана</span>
        ) : (
          <ChosenTitle id={id} />
        )}

        <Button
          size="sm"
          variant="outline"
          tone="neutral"
          onClick={() => {
            setPicking(true);
          }}
        >
          {id === '' ? 'Выбрать' : 'Заменить'}
        </Button>

        {id !== '' && (
          <Button
            iconOnly
            size="sm"
            variant="ghost"
            tone="neutral"
            aria-label="Убрать ссылку"
            onClick={() => {
              onChange(null);
            }}
          >
            <X size={14} />
          </Button>
        )}

        <Dialog open={picking} onOpenChange={setPicking}>
          {picking && (
            <RecordPicker
              collection={collection}
              chosen={ids}
              onChoose={(chosen) => {
                onChange(chosen);
                setPicking(false);
              }}
            />
          )}
        </Dialog>
      </div>
    );
  }

  const move = (from: number, to: number) => {
    const next = [...ids];
    const [item] = next.splice(from, 1);

    if (item !== undefined) {
      next.splice(to, 0, item);
      onChange(next);
    }
  };

  return (
    <div className={styles['references']}>
      {ids.length === 0 ? (
        <span className={styles['reference__empty']}>Ничего не выбрано</span>
      ) : (
        <ul className={styles['references__list']}>
          {ids.map((id, index) => (
            <li key={id} className={styles['references__item']}>
              <ChosenTitle id={id} />
              <span className={styles['references__actions']}>
                <Button
                  iconOnly
                  size="sm"
                  variant="ghost"
                  tone="neutral"
                  aria-label="Выше"
                  disabled={index === 0}
                  onClick={() => {
                    move(index, index - 1);
                  }}
                >
                  <ArrowUp size={14} />
                </Button>
                <Button
                  iconOnly
                  size="sm"
                  variant="ghost"
                  tone="neutral"
                  aria-label="Ниже"
                  disabled={index === ids.length - 1}
                  onClick={() => {
                    move(index, index + 1);
                  }}
                >
                  <ArrowDown size={14} />
                </Button>
                <Button
                  iconOnly
                  size="sm"
                  variant="ghost"
                  tone="neutral"
                  aria-label="Убрать из списка"
                  onClick={() => {
                    onChange(ids.filter((item) => item !== id));
                  }}
                >
                  <X size={14} />
                </Button>
              </span>
            </li>
          ))}
        </ul>
      )}

      <Button
        size="sm"
        variant="outline"
        tone="neutral"
        onClick={() => {
          setPicking(true);
        }}
      >
        <Plus size={14} />
        Добавить
      </Button>

      <Dialog open={picking} onOpenChange={setPicking}>
        {picking && (
          <RecordPicker
            multiple
            collection={collection}
            chosen={ids}
            onChoose={(chosen) => {
              onChange(chosen);
            }}
          />
        )}
      </Dialog>
    </div>
  );
}

function ChosenTitle({ id }: { id: string }) {
  const chosen = useQuery({
    queryKey: ['record', id],
    queryFn: async () => {
      const response = await recordsGet(id);

      return response.status === 200 ? response.data : null;
    },
  });

  return (
    <span className={styles['reference__chosen']}>
      <Link2 size={14} />
      {chosen.isPending
        ? 'Загружаем…'
        : chosen.data == null
          ? 'Запись не найдена — возможно, её удалили'
          : chosen.data.title === ''
            ? 'Без названия'
            : chosen.data.title}
    </span>
  );
}

/**
 * Выбор записи. Одиночная ссылка — щелчок выбирает и закрывает; несколько —
 * щелчок добавляет или убирает, окно остаётся открытым.
 */
function RecordPicker({
  multiple = false,
  collection,
  chosen,
  onChoose,
}: {
  multiple?: boolean;
  collection: string;
  chosen: string[];
  onChoose: (value: string | string[]) => void;
}) {
  const scope = useSiteScope();
  const all = scope?.types ?? [];
  const fixed = all.find((type) => type.slug === collection);
  const types = fixed === undefined ? all : [fixed];
  const [typeID, setTypeID] = useState(types[0]?.id ?? '');
  const [search, setSearch] = useState('');
  const debounced = useDebounced(search);
  const selected = new Set(chosen);

  const records = useQuery({
    queryKey: ['records', typeID, { search: debounced, picker: true }],
    enabled: typeID !== '',
    queryFn: async () => {
      const response = await recordsList(typeID, { search: debounced, per_page: 50 });

      return response.status === 200 ? (response.data.items ?? []) : [];
    },
  });

  return (
    <DialogContent title={multiple ? 'Выбор записей' : 'Выбор записи'} size="md">
      <div className={styles['picker']}>
        <div className={fixed === undefined ? styles['picker__bar'] : undefined}>
          {fixed === undefined && (
            <Select value={typeID} onValueChange={setTypeID}>
              {types.map((type) => (
                <SelectItem key={type.id} value={type.id}>
                  {type.name}
                </SelectItem>
              ))}
            </Select>
          )}
          <Input
            placeholder={fixed === undefined ? 'Поиск по названию' : `Поиск: ${fixed.name}`}
            aria-label="Поиск записи"
            prefix={<Search size={15} />}
            clearable
            value={search}
            onChange={(event) => {
              setSearch(event.target.value);
            }}
          />
        </div>

        {(records.data ?? []).length === 0 ? (
          <p className={styles['reference__empty']}>
            {records.isPending ? 'Загружаем…' : 'Записей не нашлось.'}
          </p>
        ) : (
          <ul className={styles['picker__list']}>
            {(records.data ?? []).map((record) => {
              const on = selected.has(record.id);

              return (
                <li key={record.id}>
                  <button
                    type="button"
                    className={styles['picker__item']}
                    aria-pressed={multiple ? on : undefined}
                    onClick={() => {
                      if (!multiple) {
                        onChoose(record.id);

                        return;
                      }

                      onChoose(
                        on ? chosen.filter((id) => id !== record.id) : [...chosen, record.id],
                      );
                    }}
                  >
                    <span className={styles['picker__title']}>
                      {multiple && (
                        <span className={styles['picker__check']} aria-hidden="true">
                          {on && <Check size={13} />}
                        </span>
                      )}
                      {record.title === '' ? 'Без названия' : record.title}
                    </span>
                    <span className={styles['reference__empty']}>
                      {record.status === 'published' ? 'на сайте' : 'черновик'}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </DialogContent>
  );
}

/** Идентификаторы значения: одно, список или пусто. */
function idsOf(value: unknown): string[] {
  if (typeof value === 'string') {
    return value === '' ? [] : [value];
  }

  return Array.isArray(value)
    ? value.filter((item): item is string => typeof item === 'string' && item !== '')
    : [];
}
